 /**
  * Class to handle Server-Sent Events (SSE) streaming
  * Could be used with fetch or with an existing Response object
  * Such as Chat streaming and TTS streaming
  */

export class SSEStreamHandler {
    private controller: AbortController | null = null;

    private reader: ReadableStreamDefaultReader<Uint8Array> | null = null;

    private isStreaming: boolean = false;

    private hasCalledDone: boolean = false;

    /**
     * Start a stream from a URL
     */
    async stream(
        url: string,
        body: any,
        onChunk: (data: any) => void,
        onDone: () => void,
        onError: (error: any) => void
    ) {
        this.abort();
        this.controller = new AbortController();
        this.isStreaming = true;
        this.hasCalledDone = false;

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Accept': 'text/event-stream'
                },
                body: JSON.stringify(body),
                signal: this.controller.signal,
            });

            await this.handleResponse(response, onChunk, onDone, onError);
        } catch (error: any) {
            SSEStreamHandler.handleError(error, onError);
        } finally {
            this.cleanup(onDone);
        }
    }

    /**
     * Process an existing Response object (useful when using APIClient)
     */
    async processResponse(
        responsePromise: Promise<Response>,
        abortController: AbortController,
        onChunk: (data: any) => void,
        onDone: () => void,
        onError: (error: any) => void
    ) {
        this.abort();
        this.controller = abortController;
        this.isStreaming = true;
        this.hasCalledDone = false;

        try {
            const response = await responsePromise;
            await this.handleResponse(response, onChunk, onDone, onError);
        } catch (error: any) {
            SSEStreamHandler.handleError(error, onError);
        } finally {
            this.cleanup(onDone);
        }
    }

    private async handleResponse(
        response: Response,
        onChunk: (data: any) => void,
        _onDone: () => void,
        _onError: (error: any) => void
    ) {
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
        }
        
        if (!response.body) throw new Error('ReadableStream not supported');

        this.reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        try {
            while (this.isStreaming) {
                const { done, value } = await this.reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    const trimmedLine = line.trim();
                    if (!trimmedLine) continue;
                    
                    if (trimmedLine.startsWith('data: ')) {
                        const data = trimmedLine.slice(6);
                        if (data === '[DONE]') {
                            // Backend signaled completion
                            return; 
                        }
                        try {
                            const parsed = JSON.parse(data);
                            onChunk(parsed);
                        } catch (e) {
                            console.error('Error parsing SSE data:', e);
                        }
                    }
                }
            }
        } finally {
            if (this.reader) {
                try {
                    this.reader.releaseLock();
                } catch (e) { /* ignore */ }
                this.reader = null;
            }
        }
    }

    private static handleError(error: any, onError: (error: any) => void) {
        if (error.name === 'AbortError' || error.message === 'Aborted') {
             console.debug('Stream aborted');
        } else {
             onError(error);
        }
    }

    private cleanup(onDone: () => void) {
        this.isStreaming = false;
        this.controller = null;
        this.reader = null;
        if (!this.hasCalledDone) {
            this.hasCalledDone = true;
            onDone();
        }
    }

    abort() {
        this.isStreaming = false;
        
        if (this.reader) {
            this.reader.cancel('Aborted').catch(() => {});
            this.reader = null;
        }

        if (this.controller) {
            this.controller.abort();
            this.controller = null;
        }
    }

    isActive(): boolean {
        return this.isStreaming;
    }
}
